import { Text } from '@ui/lib/park/ui/text';
import { queryClient } from '@ui/lib/reactQuery';
import { competitionDetailFromListQuery } from '@ui/services/backend/hooks/useTradingCompetitions/competitionDetailFromListQuery';
import { getCompetitionsQuery } from '@ui/services/backend/hooks/useTradingCompetitions/query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box,Stack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { GetCompetitionsStatusEnum } from '../../services/backend/generated';
import { useTradingCompetitions } from '../../services/backend/hooks/useTradingCompetitions/useTradingCompetitions';
import { CompetitionFilters } from './CompetitionFilters';
import { CompetitionJoinModal } from './CompetitionJoinModal';
import { CompetitionList } from './CompetitionList';
import { Competition,CompetitionFilterMode, CompetitionStatus, CompetitionType, SortOption  } from './types';

export const CompetitionPage = () => {
  const { t } = useTranslation();
  const { address, isConnected } = useAccount();
  const router = useRouter();
  const locale = typeof router.query.locale === 'string' ? router.query.locale : undefined;

  const [filterMode, setFilterMode] = useState<CompetitionFilterMode>(
    CompetitionFilterMode.ONGOING,
  );
  const [type, setType] = useState<CompetitionType | 'All'>('All');
  const [sort, setSort] = useState<SortOption>(SortOption.NEW_TO_OLD);
  const [joinTarget, setJoinTarget] = useState<Competition | null>(null);
  const didAutoPickDefaultTab = useRef(false);

  const baseQueryParams = useMemo(
    () => ({
      isReadyToDisplay: true,
      limit: 100,
      userAddress: isConnected ? address : undefined,
    }),
    [address, isConnected],
  );

  const ongoingParams = useMemo(
    () => ({ ...baseQueryParams, status: GetCompetitionsStatusEnum.Ongoing }),
    [baseQueryParams],
  );
  const upcomingParams = useMemo(
    () => ({ ...baseQueryParams, status: GetCompetitionsStatusEnum.Upcoming }),
    [baseQueryParams],
  );
  const endedParams = useMemo(
    () => ({ ...baseQueryParams, status: GetCompetitionsStatusEnum.Ended }),
    [baseQueryParams],
  );

  const queryOptions = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };

  const { data: ongoingCompetitions, isLoading: isLoadingOngoing, isFetching: isFetchingOngoing } =
    useTradingCompetitions(ongoingParams, queryOptions);
  const { data: upcomingCompetitions } = useTradingCompetitions(upcomingParams, queryOptions);
  const { data: endedCompetitions } = useTradingCompetitions(endedParams, queryOptions);

  // Compute joined competitions (across all statuses)
  const joinedCompetitions = useMemo(() => {
    const all = [
      ...(ongoingCompetitions || []),
      ...(upcomingCompetitions || []),
      ...(endedCompetitions || []),
    ];
    
return all.filter((c) => c.hasJoined);
  }, [ongoingCompetitions, upcomingCompetitions, endedCompetitions]);

  const competitions = useMemo(() => {
    switch (filterMode) {
      case CompetitionFilterMode.ONGOING:
        return ongoingCompetitions;
      case CompetitionFilterMode.UPCOMING:
        return upcomingCompetitions;
      case CompetitionFilterMode.ENDED:
        return endedCompetitions;
      case CompetitionFilterMode.JOINED:
        return joinedCompetitions;
      default:
        return ongoingCompetitions;
    }
  }, [filterMode, ongoingCompetitions, upcomingCompetitions, endedCompetitions, joinedCompetitions]);

  const isLoading = isLoadingOngoing;
  const isFetching = isFetchingOngoing;

  const statusCounts = useMemo(
    () => ({
      [CompetitionStatus.ONGOING]: ongoingCompetitions?.length ?? 0,
      [CompetitionStatus.UPCOMING]: upcomingCompetitions?.length ?? 0,
      [CompetitionStatus.ENDED]: endedCompetitions?.length ?? 0,
    }),
    [ongoingCompetitions, upcomingCompetitions, endedCompetitions],
  );

  // Auto switch default tab: if ONGOING has 0 items, show UPCOMING instead.
  // This only runs once per (address, connection) so it doesn't fight user manual tab changes.
  useEffect(() => {
    didAutoPickDefaultTab.current = false;
  }, [address, isConnected]);

  useEffect(() => {
    if (didAutoPickDefaultTab.current) return;
    if (filterMode !== CompetitionFilterMode.ONGOING) return;
    if (ongoingCompetitions === undefined) return; // wait until loaded

    if (ongoingCompetitions.length === 0) {
      setFilterMode(CompetitionFilterMode.UPCOMING);
    }

    didAutoPickDefaultTab.current = true;
  }, [filterMode, ongoingCompetitions]);

  const filteredCompetitions = useMemo(() => {
    let result = [...(competitions || [])];

    // Filter by Status (skip for JOINED since it already includes all joined)
    if (filterMode !== CompetitionFilterMode.JOINED) {
      // Cast is safe since ONGOING/UPCOMING/ENDED values match between enums
      result = result.filter((c) => c.status === (filterMode as unknown as CompetitionStatus));
    }

    // Filter by Type
    if (type !== 'All') {
      result = result.filter((c) => c.type === type);
    }

    // Sort
    result.sort((a, b) => {
      if (sort === SortOption.NEW_TO_OLD) {
        return b.startTime - a.startTime;
      } else {
        return a.startTime - b.startTime;
      }
    });

    return result;
  }, [filterMode, type, sort, competitions]);

  return (
    <Stack
      w="full"
      maxW="1120px"
      mx="auto"
      px={{ base: '12px', tablet: '24px' }}
      pt={{ base: '20px', tablet: '28px' }}
      pb={{ base: '32px', tablet: '48px' }}
      gap={{ base: '12px', tablet: '16px' }}
    >
        <CompetitionJoinModal
          open={!!joinTarget}
          onOpenChange={(open) => {
            if (!open) setJoinTarget(null);
          }}
          competition={joinTarget}
          onJoined={(competitionId) => {
            // Optimistic UI: mark joined in query cache & selected item
            const updateCache = (params: typeof ongoingParams) => {
              queryClient.setQueryData(
                getCompetitionsQuery.getKey(params),
                (prev?: Competition[]) => {
                  if (!prev) return prev;
                  
return prev.map((c) =>
                    c.id === competitionId ? { ...c, hasJoined: true } : c,
                  );
                },
              );
            };
            // Update all caches since we don't know which status the competition belongs to
            updateCache(ongoingParams);
            updateCache(upcomingParams);
            updateCache(endedParams);
          }}
        />
        <>
          {/* Header */}
          <Box>
            <Text
              as="h1"
              fontFamily="Inter"
              fontSize="24px"
              fontWeight={600}
              letterSpacing="-0.015em"
              lineHeight="1.3"
              color="v2.text.primary"
              mb="4px"
            >
              {t('competition.title', 'Trading Competition')}
            </Text>
            <Text textStyle="text.default" color="v2.text.muted">
              {t(
                'competition.subtitle',
                'Compete in PnL and volume-based trading competitions.',
              )}
            </Text>
          </Box>

          {/* Filters and List */}
          <CompetitionFilters
            currentFilterMode={filterMode}
            onFilterModeChange={setFilterMode}
            currentType={type}
            onTypeChange={setType}
            currentSort={sort}
            onSortChange={setSort}
            statusCounts={statusCounts}
            joinedCount={joinedCompetitions.length}
            isConnected={isConnected}
          />
          <CompetitionList
            competitions={filteredCompetitions}
            isLoading={isLoading || isFetching || !competitions}
            onSelect={(c) => {
              // seed detail cache so route can render without refetch
              queryClient.setQueryData(
                competitionDetailFromListQuery.getKey({
                  id: c.id,
                  userAddress: isConnected ? address : undefined,
                }),
                c,
              );

              const detailHref = locale
                ? `/${locale}/competition/${c.id}`
                : `/competition/${c.id}`;
              router.push(detailHref);
            }}
            isConnected={isConnected}
            onJoin={(c) => setJoinTarget(c)}
          />
        </>
    </Stack>
  );
};
